/**
 * <read-more> Custom Element – klemmt Text auf N Zeilen und klappt ihn auf/zu.
 * Bringt nur die Funktionalität mit; der Toggle-Button kommt aus dem Markup der
 * einbindenden Komponente (`data-read-more-toggle`).
 *
 * Verwendung, Attribute, Button-Konvention und API: siehe README.md.
 */

class ReadMore extends HTMLElement {
  static tag = "read-more";

  static {
    if (!customElements.get(ReadMore.tag)) {
      customElements.define(ReadMore.tag, ReadMore);
    }
  }

  constructor() {
    super();
    this._initialized = false;
    this._resizeObserver = null;
    this._animationSafety = null;
    this._pendingCollapse = false;
  }

  connectedCallback() {
    this._init();
  }

  disconnectedCallback() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this._animationSafety) {
      clearTimeout(this._animationSafety);
      this._animationSafety = null;
    }
  }

  open() {
    if (this.dataset.expanded !== 'true') {
      this._animate(true);
    }
  }

  close() {
    if (this.dataset.expanded === 'true') {
      this._animate(false);
    }
  }

  toggle() {
    this._animate(this.dataset.expanded !== 'true');
  }
  
  /**
   * Schaltet erst nach dem aktuellen Task um, damit das Tracking (liest beim
   * Klick interaction/label aus dem Button-Text und aria-expanded) noch den
   * Zustand zum Klick-Zeitpunkt erfasst – nicht den bereits umgeschalteten.
   */
  _toggleAfterPropagation() {
    setTimeout(this.toggle.bind(this), 0);
  }

  get expanded() {
    return this.dataset.expanded === 'true';
  }

  handleEvent(event) {
    switch (event.type) {
      case 'click':
        this._toggleAfterPropagation();
        break;
      case 'transitionend':
        if (event.propertyName === 'height') {
          this._cleanupAnimation();
        }
        break;
    }
  }

  _init() {
    if (this._initialized) {
      return;
    }

    this._btn = this.querySelector('[data-read-more-toggle]');
    if (!this._btn) {
      console.warn('<read-more>: kein [data-read-more-toggle] gefunden – Komponente bleibt im Fallback.');
      return;
    }

    const contentNodes = [];
    for (const node of Array.from(this.childNodes)) {
      if (node === this._btn) {
        continue;
      }
      contentNodes.push(node);
    }

    const contentEl = document.createElement('div');
    contentEl.className = 'read-more__content';
    for (const node of contentNodes) {
      contentEl.appendChild(node);
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'read-more__content-wrapper';
    wrapper.appendChild(contentEl);
    this.insertBefore(wrapper, this._btn);
    wrapper.addEventListener('transitionend', this);

    const live = document.createElement('span');
    live.className = 'read-more__sr';
    live.setAttribute('aria-live', 'polite');
    this.appendChild(live);

    this._wrapper = wrapper;
    this._content = contentEl;
    this._live = live;
    this._labelTarget = this._btn.querySelector('[data-read-more-label]') || this._btn;

    this.dataset.expanded = 'false';
    this._btn.setAttribute('aria-expanded', 'false');

    this._applyLines();
    this._applyDuration();
    this._applyLabel(false);

    this._btn.addEventListener('click', this);

    this.dataset.enhanced = 'true';

    this._checkClampNeeded();

    if ('ResizeObserver' in window) {
      this._resizeObserver = new ResizeObserver(this._onResize.bind(this));
      this._resizeObserver.observe(this._content);
    }

    this._recheckAfterFonts();

    this._initialized = true;
  }

  _onResize() {
    if (this.dataset.expanded === 'false' && !this.classList.contains('is-animating')) {
      this._checkClampNeeded();
    }
  }

  async _recheckAfterFonts() {
    if (!document.fonts || !document.fonts.ready) {
      return;
    }
    await document.fonts.ready;
    if (!this._initialized) {
      return;
    }
    if (this.dataset.expanded === 'false') {
      this._checkClampNeeded();
    }
  }

  _applyLines() {
    const lines = parseInt(this.getAttribute('lines'), 10);
    const value = (Number.isFinite(lines) && lines > 0) ? lines : 4;
    this.style.setProperty('--read-more-lines', value);
  }

  _applyDuration() {
    const ms = parseInt(this.getAttribute('duration'), 10);
    if (Number.isFinite(ms) && ms >= 0) {
      this.style.setProperty('--read-more-duration', ms + 'ms');
    } else {
      this.style.removeProperty('--read-more-duration');
    }
  }

  _applyLabel(isExpanded) {
    const collapsed = this._btn.dataset.labelCollapsed;
    const expanded = this._btn.dataset.labelExpanded;
    if (collapsed === undefined && expanded === undefined) {
      return;
    }
    this._labelTarget.textContent = isExpanded ? (expanded || '') : (collapsed || '');
  }

  _checkClampNeeded() {
    if (this.dataset.expanded === 'true') {
      return;
    }
    const needed = this._content.scrollHeight - this._content.clientHeight > 1;
    this.dataset.clampNeeded = needed ? 'true' : 'false';
    this._btn.hidden = !needed;
  }

  _animate(toExpanded) {
    if (prefersReducedMotion()) {
      this.dataset.expanded = toExpanded ? 'true' : 'false';
      this._syncToggleState(toExpanded);
      return;
    }

    const wrapper = this._wrapper;

    const startHeight = wrapper.getBoundingClientRect().height;
    wrapper.style.height = startHeight + 'px';

    this.classList.add('is-animating');
    this.dataset.expanded = toExpanded ? 'true' : 'false';

    let endHeight;
    if (toExpanded) {
      wrapper.style.height = 'auto';
      endHeight = wrapper.getBoundingClientRect().height;
    } else {
      this.classList.remove('is-animating');
      wrapper.style.height = 'auto';
      endHeight = wrapper.getBoundingClientRect().height;
      this.classList.add('is-animating');
    }

    wrapper.style.height = startHeight + 'px';
    void wrapper.offsetHeight;
    const dur = this.style.getPropertyValue('--read-more-duration') || '350ms';
    wrapper.style.transition = `height ${dur} cubic-bezier(0.4, 0, 0.2, 1)`;
    wrapper.style.height = endHeight + 'px';

    this._syncToggleState(toExpanded);

    this._pendingCollapse = !toExpanded;
    const safetyMs = parseInt(dur, 10) + 250;
    this._animationSafety = setTimeout(this._cleanupAnimation.bind(this), safetyMs);
  }

  _syncToggleState(toExpanded) {
    this._btn.setAttribute('aria-expanded', toExpanded ? 'true' : 'false');
    this._applyLabel(toExpanded);
    this._live.textContent = toExpanded
      ? 'Vollständiger Text angezeigt.'
      : 'Text eingeklappt.';
    this.dispatchEvent(new CustomEvent('read-more:toggle', {
      bubbles: true,
      detail: { expanded: toExpanded }
    }));
  }

  _cleanupAnimation() {
    if (!this.classList.contains('is-animating')) {
      return;
    }
    if (this._animationSafety) {
      clearTimeout(this._animationSafety);
      this._animationSafety = null;
    }

    this._wrapper.style.transition = '';
    this._wrapper.style.height = '';
    this.classList.remove('is-animating');

    if (this._pendingCollapse) {
      const rect = this._btn.getBoundingClientRect();
      if (rect.top < 0) {
        this._scrollIntoViewSafely();
      }
    }
  }

  _scrollIntoViewSafely() {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.scrollIntoView({ behavior, block: 'start' });
  }
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}